import React, { CSSProperties, forwardRef, MouseEvent, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { useIntl } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import { ReactComponent as ClosedWonImage } from "@app/assets/dashboard/closed-won.svg";
import Chip from "@app/component/Chip";
import EntityBadge from "@app/component/EntityBadge";
import EntityChip from "@app/component/EntityChip";
import FieldGrid from "@app/component/FieldGrid";
import { getFunnelStages } from "@app/store/deal";
import { showEntityView } from "@app/store/entityView/actions";
import EntityViewPayload from "@app/store/entityView/EntityViewPayload";
import RecordPreviewConfigurationColumns from "@app/store/recordPreview/RecordPreviewConfigurationColumns";
import { getRecordPreviewConfigurationColumns } from "@app/store/recordPreview/selectors";
import { RootState } from "@app/store/rootReducer";
import { TimelineEventUpdatedDeal } from "@app/types/events/TimelineEvent";
import isClosedStage from "@app/util/isClosedStage";

import BaseEvent from "./BaseEvent";
import styles from "./RecordEvent.module.scss";

export interface RecordEventProps {
  event: TimelineEventUpdatedDeal;
  expanded: boolean;
  onSizeChange?: () => void;
  style?: CSSProperties;
}

interface Props extends RecordEventProps {
  columns: RecordPreviewConfigurationColumns;
  funnelStages: Record<Funnel["id"], Stage[]>;
  onShowEntityView: (payload: EntityViewPayload) => void;
}

const StageChangedEvent = forwardRef<HTMLDivElement, Props>(
  ({ columns, event, expanded, funnelStages, onShowEntityView, onSizeChange, style }, ref) => {
    const intl = useIntl();

    const record = event.entity;
    const stages = useMemo(() => Object.values(funnelStages).flat(), [funnelStages]);
    const dealStageChange = event.updated?.find((item) => item.dealStageId)?.dealStageId;

    const handleShowEntity = useCallback(
      (payload: EntityViewPayload, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        onShowEntityView(payload);
      },
      [onShowEntityView]
    );

    if (!dealStageChange) {
      return null;
    }
    const oldStage = stages.find(({ id }) => (dealStageChange.old as Stage["id"]) === id)!;
    const newStage = stages.find(({ id }) => (dealStageChange.new as Stage["id"]) === id)!;

    return (
      <BaseEvent
        date={event.updatedAt}
        expanded={expanded}
        extra={newStage.type === DealStageType.WON ? <ClosedWonImage /> : undefined}
        extraClassName={cn({ [styles.dealWonExtraContainer]: newStage.type === DealStageType.WON })}
        onSizeChange={onSizeChange}
        ref={ref}
        style={style}
        title={intl.formatMessage(
          {
            id: "dashboard.timeline.event.dealStageChanged",
            defaultMessage:
              "{closedStage, select, true {marked {deal} as {newStage}} other {changed the stage of {deal}}}",
            description: "Title of the Deal Stage changed event item",
          },
          {
            closedStage: isClosedStage(newStage),
            deal: (
              <EntityChip
                entityId={record.id}
                entityType={record.entity}
                inline
                name={record.name}
                onClick={handleShowEntity}
              />
            ),
            newStage: (
              <Chip inline>
                <EntityBadge entity={newStage} />
              </Chip>
            ),
          }
        )}
        user={event.updatedBy}
      >
        {isClosedStage(newStage) && columns[record.entity].length ? (
          <FieldGrid
            className={styles.grid}
            columns={columns[record.entity]}
            entity={record}
            showEmpty
          />
        ) : (
          <div className={styles.stageChangedContainer}>
            <Chip inline>
              <EntityBadge entity={oldStage} />
            </Chip>
            →
            <Chip inline>
              <EntityBadge entity={newStage} />
            </Chip>
          </div>
        )}
      </BaseEvent>
    );
  }
);

const mapStateToProps = (state: RootState) => ({
  columns: getRecordPreviewConfigurationColumns(state),
  funnelStages: getFunnelStages(state),
});

const mapDispatchToProps = {
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  StageChangedEvent
);
